// [Codex 2026-07-04] Clear stale CAO todo pills after idle time or new-chat scope resets.
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { shouldMigrateDraftWorkStorage } from "./workStorageScope";

export type IssueTrackingType =
  "task" | "bug" | "feature" | "risk" | "chore" | "issue";

export interface TodoItem {
  id: string;
  text: string;
  done: boolean;
  createdAt: number;
  issueTrackingType?: IssueTrackingType;
  source?: string;
}

export interface AddTodoOptions {
  issueTrackingType?: IssueTrackingType;
  source?: string;
}

export interface ClearTodoOptions {
  extraStorageKeys?: string[];
}

export interface UseTodoListOptions {
  idleClearMs?: number;
  isIdleTimerPaused?: boolean;
}

export const TODO_IDLE_CLEAR_MS = 5 * 60 * 1000;

const LEGACY_STORAGE_KEY = "fintheon:todo-list";
const DEFAULT_STORAGE_KEY = "fintheon:todo-list:v2:default";
const TODO_IDLE_EVENTS = [
  "pointerdown",
  "keydown",
  "touchstart",
  "wheel",
  "focus",
] as const;

function removeSaved(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {}
}

function removeLegacySaved(): void {
  removeSaved(LEGACY_STORAGE_KEY);
}

function readSaved(storageKey: string): TodoItem[] {
  try {
    removeLegacySaved();
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const saved = JSON.parse(raw) as TodoItem[];
      const filtered = saved.filter(
        (item) => !item.id.startsWith("todo_seed_"),
      );
      if (filtered.length !== saved.length) persist(filtered, storageKey);
      return filtered;
    }
    return [];
  } catch {
    return [];
  }
}

function persist(items: TodoItem[], storageKey: string): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(items));
  } catch {}
}

export function useTodoList(
  storageKey = DEFAULT_STORAGE_KEY,
  options: UseTodoListOptions = {},
) {
  const activeStorageKey = useMemo(
    () => storageKey || DEFAULT_STORAGE_KEY,
    [storageKey],
  );
  const { idleClearMs = TODO_IDLE_CLEAR_MS, isIdleTimerPaused = false } =
    options;
  const storageKeyRef = useRef(activeStorageKey);
  const idleTimerRef = useRef<number | null>(null);
  const [todos, setTodos] = useState<TodoItem[]>(() =>
    readSaved(activeStorageKey),
  );
  const [loadedStorageKey, setLoadedStorageKey] = useState(activeStorageKey);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current === null) return;
    window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
  }, []);

  const scheduleIdleClear = useCallback(() => {
    clearIdleTimer();
    if (idleClearMs <= 0 || isIdleTimerPaused) return;
    idleTimerRef.current = window.setTimeout(() => {
      setTodos((current) => {
        if (current.length === 0) return current;
        persist([], storageKeyRef.current);
        return [];
      });
    }, idleClearMs);
  }, [clearIdleTimer, idleClearMs, isIdleTimerPaused]);

  useEffect(() => {
    removeLegacySaved();
    const previousKey = storageKeyRef.current;
    clearIdleTimer();
    if (previousKey === activeStorageKey) {
      setLoadedStorageKey(activeStorageKey);
      return;
    }
    storageKeyRef.current = activeStorageKey;
    const saved = readSaved(activeStorageKey);
    setTodos((current) => {
      if (
        shouldMigrateDraftWorkStorage(previousKey, activeStorageKey) &&
        saved.length === 0 &&
        current.length > 0
      ) {
        persist(current, activeStorageKey);
        removeSaved(previousKey);
        return current;
      }
      return saved;
    });
    setLoadedStorageKey(activeStorageKey);
  }, [activeStorageKey, clearIdleTimer]);

  useEffect(() => {
    if (
      loadedStorageKey !== activeStorageKey ||
      todos.length === 0 ||
      isIdleTimerPaused
    ) {
      clearIdleTimer();
      return;
    }
    scheduleIdleClear();
    for (const eventName of TODO_IDLE_EVENTS) {
      window.addEventListener(eventName, scheduleIdleClear, { passive: true });
    }
    return () => {
      clearIdleTimer();
      for (const eventName of TODO_IDLE_EVENTS) {
        window.removeEventListener(eventName, scheduleIdleClear);
      }
    };
  }, [
    activeStorageKey,
    clearIdleTimer,
    isIdleTimerPaused,
    loadedStorageKey,
    scheduleIdleClear,
    todos.length,
  ]);

  const addTodo = useCallback((text: string, options?: AddTodoOptions) => {
    setTodos((prev) => {
      const next = [
        ...prev,
        {
          id: `todo_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          text,
          done: false,
          createdAt: Date.now(),
          issueTrackingType: options?.issueTrackingType,
          source: options?.source,
        },
      ];
      persist(next, storageKeyRef.current);
      return next;
    });
  }, []);

  const toggleTodo = useCallback((id: string) => {
    setTodos((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t));
      persist(next, storageKeyRef.current);
      return next;
    });
  }, []);

  const removeTodo = useCallback((id: string) => {
    setTodos((prev) => {
      const next = prev.filter((t) => t.id !== id);
      persist(next, storageKeyRef.current);
      return next;
    });
  }, []);

  const clearDone = useCallback(() => {
    setTodos((prev) => {
      const next = prev.filter((t) => !t.done);
      persist(next, storageKeyRef.current);
      return next;
    });
  }, []);

  const clearTodos = useCallback((options?: ClearTodoOptions) => {
    setTodos([]);
    const keys = [
      storageKeyRef.current,
      ...(options?.extraStorageKeys ?? []),
    ].filter((key, index, list) => list.indexOf(key) === index);
    for (const key of keys) persist([], key);
  }, []);

  const scopedTodos = loadedStorageKey === activeStorageKey ? todos : [];

  return {
    todos: scopedTodos,
    addTodo,
    toggleTodo,
    removeTodo,
    clearDone,
    clearTodos,
    isStorageLoaded: loadedStorageKey === activeStorageKey,
  };
}
